import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import VitlyHeroSection from '../components/home/VitlyHeroSection';
import { SEO } from '../components/SEO';
// TestimonialCard removed for launch - will add real testimonials post-launch
import Section from '../components/ui/Section';
import { 
  MapPinIcon, 
  UsersIcon, 
  SparklesIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  DocumentDuplicateIcon,
  CheckIcon,
  XMarkIcon,
  ArrowRightIcon,
  BoltIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const HomePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  
  useEffect(() => {
    // Check for OAuth error parameters and clean them from URL
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    const errorCode = params.get('error_code');
    const errorDescription = params.get('error_description');
    
    if (error || errorCode) {
      console.log('[HomePage] OAuth error detected, cleaning URL:', { error, errorCode, errorDescription });
      
      // Clean OAuth error parameters from URL
      navigate('/', { replace: true });
      
      // Clear any stale OAuth state
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_csrf');
      sessionStorage.removeItem('oauth_timestamp');
      
      // Show a user-friendly notification if needed
      if (error === 'invalid_request' && errorCode === 'bad_oauth_state') {
        // This typically happens when the OAuth flow was interrupted or expired
        console.log('[HomePage] OAuth state mismatch - flow may have expired or been interrupted');
      }
    }
  }, [location, navigate]);
  // Structured data for the homepage
  const homeStructuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebApplication',
        'name': 'Vitly',
        'description': 'Smart meetup coordination platform',
        'url': 'https://www.getvitly.com',
        'applicationCategory': 'SocialNetworkingApplication',
        'operatingSystem': 'Web',
        'offers': {
          '@type': 'Offer',
          'price': '0',
          'priceCurrency': 'USD'
        }
      },
      {
        '@type': 'FAQPage',
        'mainEntity': [
          {
            '@type': 'Question',
            'name': 'How does Vitly find the perfect meeting spot?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Vitly uses smart algorithms to analyze participant locations and find the optimal midpoint that minimizes travel time for everyone. It considers factors like distance and venue ratings to suggest the most convenient location for all participants.'
            }
          },
          {
            '@type': 'Question',
            'name': 'Is Vitly free to use?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Yes, Vitly offers a free tier for personal use with all core features included. You can create meetups, invite participants, and get smart location suggestions without any cost.'
            }
          },
          {
            '@type': 'Question',
            'name': 'How many people can participate in a meetup?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Vitly supports meetups of any size, from one-on-one meetings to large group gatherings. The platform efficiently calculates optimal locations regardless of the number of participants.'
            }
          },
          {
            '@type': 'Question',
            'name': 'Does Vitly work internationally?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Yes, Vitly works globally. Our location services and venue recommendations are available worldwide, powered by Google Maps and Places API.'
            }
          },
          {
            '@type': 'Question',
            'name': 'Can I save my favorite meeting spots?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Yes, Vitly allows you to save and manage your favorite venues. You can create personal favorites, group favorites for specific meetups, and access popular spots used by the community.'
            }
          }
        ]
      }
    ]
  };

  const faqs = [
    {
      question: "What is the instant fairness preview?",
      answer: "Drop everyone’s locations and Vitly immediately calculates a fairness score, travel variance, and a midpoint so you can make a call in seconds."
    },
    {
      question: "Is there a free plan?",
      answer: "Yes. Hangout Essentials includes 3 meetups per month with the instant fairness preview, guest summary links, and basic engagement snapshots. Upgrade to Social Squad for more meetups, templates, and advanced analytics."
    },
    {
      question: "Do participants need to create accounts?",
      answer: "No. Share the Vitly summary link and guests can RSVP, vote, and navigate without signing up. Creating meetups still requires an account."
    },
    {
      question: "Which travel modes does Vitly support?",
      answer: "Vitly currently calculates driving routes and travel times, flagging any outliers so every participant is considered fairly."
    },
    {
      question: "Can I reuse successful meetups?",
      answer: "You can quickly create new meetups using system templates for common gathering types like coffee, dinner, or activities. Social Squad members also get automatic reminders and advanced analytics."
    },
    {
      question: "How secure is my location data?",
      answer: "Vitly uses Supabase Row-Level Security (RLS), TLS encryption in transit, and database-level access controls. Location data powers the fairness algorithm and is never sold."
    },
    {
      question: "Does Vitly work internationally?",
      answer: "Vitly supports coordination across cities and regions wherever Google Maps data is available. We’re expanding localized venue insights every month."
    },
    {
      question: "How accurate are the travel estimates?",
      answer: "Travel estimates leverage live Google data and our own variance checks to stay current, with manual overrides available for edge cases."
    }
  ];

  const features = [
    {
      icon: <BoltIcon className="w-6 h-6" />,
      title: "Instant Fairness Preview",
      description: "Drop participant locations and see Vitly calculate a midpoint, fairness score, and travel variance in seconds."
    },
    {
      icon: <ChartBarIcon className="w-6 h-6" />,
      title: "Travel & Engagement Analytics",
      description: "View travel times for all participants, track meetup activity trends, and monitor RSVP responses."
    },
    {
      icon: <DocumentDuplicateIcon className="w-6 h-6" />,
      title: "Quick-Start Templates",
      description: "Choose from preset templates for coffee meetups, dinners, activities, and more to speed up planning."
    },
    {
      icon: <UsersIcon className="w-6 h-6" />,
      title: "Guest-Friendly Invitations",
      description: "Send summary links that let participants weigh in without creating accounts or downloading an app."
    },
    {
      icon: <SparklesIcon className="w-6 h-6" />,
      title: "Curated Venue Intelligence",
      description: "Blend ratings, budget, accessibility, and context to recommend the right spot for every occasion."
    },
    {
      icon: <ShieldCheckIcon className="w-6 h-6" />,
      title: "Enterprise-Grade Safeguards",
      description: "Supabase auth, audit logs, rate limiting, and privacy controls keep sensitive location data secure."
    },
    {
      icon: <DevicePhoneMobileIcon className="w-6 h-6" />,
      title: "On-the-Go Coordination",
      description: "Mobile-ready dashboards, push notifications, and quick-share summaries meet organizers wherever they are."
    },
    {
      icon: <MapPinIcon className="w-6 h-6" />,
      title: "Hybrid Team Ready",
      description: "Coordinate across neighborhoods or regions with real-time travel calculations and Google Maps integration."
    }
  ];

  const problems = [
    "Manual midpoint searches burn time you don't have",
    "Travel feels unfair and team morale drops",
    "Invites, RSVPs, and notes live across scattered tools",
    "Venue research means juggling tabs and reviews",
    "No proof for leadership that coordination work paid off"
  ];

  const solutions = [
    "Preview fairness in under 10 seconds",
    "Balance travel time for every participant automatically",
    "Coordinate invites, guest responses, and agendas in one place",
    "Rely on curated venue intelligence backed by real data",
    "Track meetup activity and share travel time breakdowns with your group"
  ];

  const steps = [
    {
      number: 1,
      title: "Drop Locations & Priorities",
      description: "Add participant locations, travel modes, and must-haves. Guests can contribute without creating accounts.",
      icon: <MapPinIcon className="w-8 h-8" />
    },
    {
      number: 2,
      title: "Review the Fairness Preview",
      description: "Vitly instantly surfaces the fairest midpoint, curated venues, and travel variance so you can decide with confidence.",
      icon: <BoltIcon className="w-8 h-8" />
    },
    {
      number: 3,
      title: "Share & Track the Plan",
      description: "Send guest-friendly summaries, reuse templates, and monitor analytics as RSVPs roll in.",
      icon: <ChartBarIcon className="w-8 h-8" />
    }
  ];

  const useCases = [
    {
      category: "Professional Organizers",
      items: ["Hybrid team standups", "Client roadshows", "Regional offsites", "Sales ride-alongs"]
    },
    {
      category: "Social Hosts",
      items: ["Friends weekends", "Birthday dinners", "Brunch clubs", "Holiday traditions"]
    },
    {
      category: "Time-Strapped Planners",
      items: ["Visiting client meetings", "Layover catch-ups", "Campus meetups", "Family stopovers"]
    },
    {
      category: "Community Leaders",
      items: ["Volunteer events", "Book & hobby clubs", "Alumni gatherings", "Neighborhood councils"]
    }
  ];

  return (
    <>
      <SEO 
        title="Vitly - Instant Fairness Preview & Pro Meetup Coordination"
        description="Vitly delivers instant fairness previews, curated venue intelligence, reusable templates, and analytics so every meetup stays equitable and organized."
        keywords="fairness preview, meetup coordination platform, travel analytics, venue intelligence, organizer templates, vitly"
        structuredData={homeStructuredData}
      />
      <main className="min-h-screen" role="main" itemScope itemType="https://schema.org/WebApplication">
      {/* Hero Section */}
      <VitlyHeroSection />

      {/* Problem/Solution Section */}
      <Section variant="secondary">
          <h2 className="text-3xl lg:text-4xl font-bold text-center text-gray-900 dark:text-gray-100 mb-6">
            Manual coordination steals hours and trust
          </h2>
          <p className="text-lg text-center text-gray-600 dark:text-gray-300 mb-12 max-w-2xl mx-auto">
            Vitly replaces endless threads with instant fairness insights, curated venues, and share-ready plans that keep everyone aligned.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
              <h3 className="text-2xl font-semibold mb-6 text-red-900 dark:text-red-200">Without Vitly 😫</h3>
              <ul className="space-y-3">
                {problems.map((problem, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <XMarkIcon className="w-5 h-5 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{problem}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6">
              <h3 className="text-2xl font-semibold mb-6 text-green-900 dark:text-green-200">With Vitly ✨</h3>
              <ul className="space-y-3">
                {solutions.map((solution, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckIcon className="w-5 h-5 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{solution}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="text-center mt-8">
            <Link to="/features" className="text-[var(--premium-accent-cta,#0070f3)] hover:text-[color-mix(in_srgb,var(--premium-accent-cta)_80%,black)] font-medium text-lg">
              See the Difference <ArrowRightIcon className="inline w-4 h-4 ml-1" />
            </Link>
          </div>
      </Section>

      {/* How It Works Section */}
      <Section variant="primary">
          <h2 className="text-3xl lg:text-4xl font-bold text-center mb-4">
            From dropped pins to data-backed decisions in minutes
          </h2>
          <p className="text-lg text-center text-gray-600 dark:text-gray-300 mb-12 max-w-2xl mx-auto">
            Skip the debates. Vitly guides you from instant fairness preview to a fully coordinated plan.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {steps.map((step, idx) => (
              <div key={idx} className="relative premium-card p-6">
                {idx < steps.length - 1 && (
                  <div className="hidden md:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-blue-300 to-transparent -z-10" />
                )}
                
                <div className="group bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="w-20 h-20 mx-auto mb-4 bg-[var(--premium-elevated,#f3f4f6)] rounded-full flex items-center justify-center text-white transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <span className="text-2xl font-bold">{step.number}</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 group-hover:text-[color-mix(in_srgb,var(--premium-accent-cta)_80%,black)] transition-colors">{step.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/register" className="premium-cta text-white px-8 py-3 text-lg rounded-lg font-semibold transition-colors" style={{backgroundColor:'var(--premium-accent-cta, #0070f3)'}}>
              Plan Your First Meetup Free
            </Link>
          </div>
      </Section>

      {/* Features Grid */}
      <Section variant="secondary">
          <h2 className="text-3xl lg:text-4xl font-bold text-center mb-4">
            Why organizers choose Vitly
          </h2>
          <p className="text-lg text-center text-gray-600 dark:text-gray-300 mb-12 max-w-2xl mx-auto">
            Every capability is built to deliver instant clarity, equitable travel, and organized follow-through.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {features.map((feature, idx) => (
              <div 
                key={idx} 
                className="group bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl p-6 hover:shadow-xl hover:border-[var(--premium-accent-cta,#0070f3)]/30 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
              >
                <div className="text-[var(--premium-accent-cta,#0070f3)] mb-4 transform group-hover:scale-110 transition-transform duration-300">{feature.icon}</div>
                <h3 className="text-lg font-semibold mb-2 group-hover:text-[color-mix(in_srgb,var(--premium-accent-cta)_80%,black)] transition-colors">{feature.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{feature.description}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/features" className="text-[var(--premium-accent-cta,#0070f3)] hover:text-[color-mix(in_srgb,var(--premium-accent-cta)_80%,black)] font-medium text-lg">
              Explore All Features <ArrowRightIcon className="inline w-4 h-4 ml-1" />
            </Link>
          </div>
      </Section>

      {/* Use Cases Section */}
      <Section variant="primary">
          <h2 className="text-3xl lg:text-4xl font-bold text-center mb-12">
            Built for teams, friends, and communities
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {useCases.map((useCase, idx) => (
              <div key={idx} className="group bg-white dark:bg-gray-800 rounded-xl p-6 hover:shadow-lg hover:border hover:border-[var(--premium-accent-cta,#0070f3)]/20 transition-all duration-300">
                <h3 className="text-xl font-semibold mb-4 text-[var(--premium-accent-cta,#0070f3)] group-hover:text-[color-mix(in_srgb,var(--premium-accent-cta)_80%,black)] transition-colors">{useCase.category}</h3>
                <ul className="space-y-2">
                  {useCase.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex items-start gap-2 group/item hover:translate-x-1 transition-transform duration-200">
                      <CheckIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0 group-hover/item:scale-125 transition-transform" />
                      <span className="text-gray-700 dark:text-gray-300 text-sm group-hover/item:text-gray-900 dark:text-gray-100 transition-colors">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/use-cases" className="text-[var(--premium-accent-cta,#0070f3)] hover:text-[color-mix(in_srgb,var(--premium-accent-cta)_80%,black)] font-medium text-lg">
              Explore more use cases <ArrowRightIcon className="inline w-4 h-4 ml-1" />
            </Link>
          </div>
      </Section>

      {/* Testimonials Section - Removed for launch, will add real testimonials post-launch */}

      {/* FAQ Section */}
      <Section variant="primary" as="section" id="faq" className="[&]:itemscope [&]:[itemtype='https://schema.org/FAQPage']">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-center text-gray-900 dark:text-gray-100 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 text-center mb-12">
              Everything you need to know about hanging out smarter
            </p>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600" itemScope itemType="https://schema.org/Question">
                  <button
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 dark:bg-gray-700 transition-colors rounded-lg"
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    itemProp="name"
                  >
                    <span className="text-lg font-semibold text-gray-900 dark:text-gray-100 pr-4">
                      {faq.question}
                    </span>
                    <ChevronDownIcon 
                      className={`w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-transform ${
                        openFAQ === index ? 'transform rotate-180' : ''
                      }`} 
                    />
                  </button>
                  {openFAQ === index && (
                    <div className="px-6 pb-4" itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed" itemProp="text">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <p className="text-gray-600 dark:text-gray-300 mb-4">Still have questions?</p>
              <Link 
                to="/contact" 
                className="inline-flex items-center text-[var(--premium-accent-cta,#0070f3)] font-semibold hover:text-[color-mix(in_srgb,var(--premium-accent-cta)_70%,#111827)] transition-colors"
              >
                Contact our support team
                <ArrowRightIcon className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
      </Section>

      {/* Final CTA */}
      <Section variant="cta">
          <div className="text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4 animate-fade-in">
              Ready to End Group Chat Chaos?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Your friends will thank you for making hangouts easier
            </p>
            <Link to="/register" className="group inline-block premium-cta text-white px-8 py-3 rounded-lg font-semibold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300" style={{backgroundColor:'var(--premium-accent-cta, #0070f3)'}}>
              Plan Your First Meetup Free
              <svg className="inline-block w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
            <div className="mt-6 text-white/80">
              <span className="inline-flex items-center gap-4">
                <span>🚀 Ready in 30 seconds</span>
                <span>💸 Free to start</span>
                <span>🎉 Your friends will love it</span>
              </span>
            </div>
          </div>
      </Section>
    </main>
    </>
  );
};

export default HomePage;
